import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import CareRoom from "@/components/CareRoom";
import { PatientMedicalRecords } from "@/components/MedicalRecords";
import {
  Star, Video, Calendar as CalIcon, Clock, Loader2, CheckCircle2,
  QrCode, ArrowLeft, ArrowRight, X, RefreshCw, Smartphone, CreditCard, MapPin,
} from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
const statusLabel = { pending_payment: "payment due", booked: "booked", waiting: "waiting", admitted: "admitted", in_call: "in call", completed: "completed", cancelled: "cancelled" };
const statusStyle = {
  pending_payment: "bg-red-100 text-red-600", booked: "bg-slate-100 text-slate-600",
  waiting: "bg-amber-100 text-[#92400E]", admitted: "bg-green-100 text-green-700",
  in_call: "bg-green-100 text-green-700", completed: "bg-slate-100 text-slate-500",
  cancelled: "bg-slate-100 text-slate-400",
};

/* ---------- Calendar + time-slot picker ---------- */
function SlotPicker({ slots, value, onChange }) {
  const future = slots.filter((s) => new Date(s.start_time) > new Date());
  const availableDays = new Set(future.map((s) => new Date(s.start_time).toDateString()));
  const [date, setDate] = useState(() => (future[0] ? new Date(future[0].start_time) : new Date()));
  const daySlots = future.filter((s) => new Date(s.start_time).toDateString() === date.toDateString());

  if (future.length === 0) return <p className="text-sm text-slate-400">No open slots. Please check back later.</p>;

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="rounded-xl border border-slate-200 p-2">
        <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)}
          disabled={(day) => !availableDays.has(day.toDateString())} className="p-0" />
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-600">Times on {date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</Label>
        <div className="mt-2 grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
          {daySlots.length === 0 ? <span className="text-sm text-slate-400 col-span-2">No times this day.</span>
            : daySlots.map((s) => (
              <button key={s.id} data-testid={`slot-${s.id}`} onClick={() => onChange(s.id)}
                className={`text-sm px-3 py-2.5 rounded-xl border transition-colors ${value === s.id ? "border-[#F59E0B] bg-amber-50 text-[#92400E]" : "border-slate-200 hover:border-slate-300"}`}>
                <Clock className="w-3.5 h-3.5 inline mr-1" />{fmtTime(s.start_time)}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Payment (UPI) ---------- */
function PaymentView({ appointment, payment, onPaid }) {
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!ref.trim()) { toast.error("Enter the UPI reference / UTR number"); return; }
    setBusy(true);
    try { await api.post(`/appointments/${appointment.id}/confirm-payment`, { reference: ref }); toast.success("Payment recorded — appointment confirmed"); onPaid(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5 text-[#D97706]" /><h3 className="font-head text-xl font-semibold text-[#041E26]">Pay ₹{payment.amount} to confirm</h3></div>
      <p className="text-sm text-slate-500 mb-5">Appointment with {appointment.doctor_name} · {fmt(appointment.slot_time)}</p>
      {payment.configured ? (
        <div className="grid sm:grid-cols-2 gap-5 items-center">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
            <img src={payment.qr} alt="UPI QR" data-testid="upi-qr" className="w-44 h-44 mx-auto" />
            <p className="text-xs text-slate-400 mt-2">Scan with any UPI app</p>
          </div>
          <div>
            <div className="text-sm"><span className="text-slate-400">Pay to</span><div className="font-semibold text-[#041E26]">{payment.payee}</div><div className="font-mono-c text-sm text-[#041E26]">{payment.upi_id}</div></div>
            <a href={payment.link} data-testid="upi-link" className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#041E26] text-white text-sm font-semibold"><Smartphone className="w-4 h-4" /> Open UPI app</a>
            <p className="text-xs text-slate-400 mt-3">Amount: <b className="text-[#041E26]">₹{payment.amount}</b></p>
          </div>
        </div>
      ) : <p className="text-sm text-red-500">UPI not configured.</p>}
      <div className="mt-6">
        <Label className="text-xs font-semibold text-slate-600">Enter UPI reference / UTR number after paying</Label>
        <Input data-testid="upi-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 4198XXXXXX12" className="mt-1.5 h-12 rounded-xl" />
      </div>
      <Button data-testid="confirm-payment-final-btn" onClick={confirm} disabled={busy} className="w-full mt-5 h-12 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> I&apos;ve paid — confirm</>}
      </Button>
      <p className="text-[11px] text-slate-400 mt-3 text-center">Payment is verified manually by our team. Swappable to Razorpay auto-verification later.</p>
    </div>
  );
}

/* ---------- Booking dialog (select -> pay) ---------- */
function BookingDialog({ provider, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [symptoms, setSymptoms] = useState("");
  const [step, setStep] = useState("select");
  const [appt, setAppt] = useState(null);
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/providers/${provider.id}`).then(({ data }) => setDetail(data)).catch(() => {}); }, [provider.id]);

  const proceed = async () => {
    if (!slotId) { toast.error("Pick a time slot"); return; }
    if (!symptoms.trim()) { toast.error("Describe your symptoms"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/appointments", { doctor_id: provider.id, slot_id: slotId, symptoms });
      setAppt(data.appointment); setPayment(data.payment);
      if (data.appointment.fee > 0) setStep("pay"); else { toast.success("Appointment booked"); onDone(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent data-testid="booking-dialog" className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <img src={provider.photo} alt="" className="w-14 h-14 rounded-full object-cover" />
          <div>
            <h3 className="font-head text-lg font-semibold text-[#041E26]">{provider.name}</h3>
            <p className="text-sm text-[#D97706]">{provider.specialty} · ₹{provider.fee}</p>
            {(provider.address_city || provider.address_line) && (
              <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3 text-[#B45309]" />{[provider.address_line, provider.address_city, provider.address_state, provider.address_pincode].filter(Boolean).join(", ")}</p>
            )}
          </div>
        </div>
        {step === "select" ? (
          <>
            {!detail ? <p className="text-sm text-slate-400">Loading availability…</p> : (
              <>
                <Label className="text-xs font-semibold text-slate-600 mb-2 block">Pick a date & time</Label>
                <SlotPicker slots={detail.slots} value={slotId} onChange={setSlotId} />
              </>
            )}
            <Label className="text-xs font-semibold text-slate-600 mt-5 block">Describe your symptoms</Label>
            <textarea data-testid="symptoms-input" value={symptoms} onChange={(e) => setSymptoms(e.target.value)} rows={3}
              placeholder="e.g. Fever and sore throat since 2 days…" className="mt-1.5 w-full rounded-xl border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B]" />
            <Button data-testid="proceed-payment-btn" onClick={proceed} disabled={busy} className="w-full mt-5 h-12 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Proceed to payment <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </>
        ) : (
          <PaymentView appointment={appt} payment={payment} onPaid={onDone} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Complete-payment dialog (for pending appts) ---------- */
function CompletePaymentDialog({ appointment, onClose, onDone }) {
  const [payment, setPayment] = useState(null);
  useEffect(() => { api.get(`/appointments/${appointment.id}/payment`).then(({ data }) => setPayment(data.payment)).catch(() => {}); }, [appointment.id]);
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent data-testid="complete-payment-dialog" className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
        {payment ? <PaymentView appointment={appointment} payment={payment} onPaid={onDone} /> : <p className="text-sm text-slate-400">Loading…</p>}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Reschedule dialog ---------- */
function RescheduleDialog({ appointment, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/providers/${appointment.doctor_id}`).then(({ data }) => setDetail(data)).catch(() => {}); }, [appointment.doctor_id]);
  const save = async () => {
    if (!slotId) { toast.error("Pick a new time"); return; }
    setBusy(true);
    try { await api.post(`/appointments/${appointment.id}/reschedule`, { slot_id: slotId }); toast.success("Appointment rescheduled"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent data-testid="reschedule-dialog" className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-head text-xl font-semibold text-[#041E26] mb-1">Reschedule appointment</h3>
        <p className="text-sm text-slate-500 mb-4">{appointment.doctor_name} · currently {fmt(appointment.slot_time)}</p>
        {!detail ? <p className="text-sm text-slate-400">Loading availability…</p> : <SlotPicker slots={detail.slots} value={slotId} onChange={setSlotId} />}
        <Button data-testid="confirm-reschedule-btn" onClick={save} disabled={busy} className="w-full mt-5 h-12 rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white font-bold gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Confirm new time</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Waiting room ---------- */
function WaitingRoom({ appointment, onClose }) {
  const [status, setStatus] = useState(appointment.status);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let active = true;
    api.post(`/appointments/${appointment.id}/request-join`).catch((e) => { toast.error(e?.response?.data?.detail || "Cannot join"); onClose(false); });
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get(`/appointments/${appointment.id}`);
        if (!active) return; setStatus(data.appointment.status);
        if (["admitted", "in_call"].includes(data.appointment.status)) { clearInterval(poll); setReady(true); }
      } catch { /* ignore */ }
    }, 3000);
    return () => { active = false; clearInterval(poll); };
  }, [appointment.id]);
  if (ready) return <CareRoom appointment={appointment} onClose={onClose} />;
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="waiting-room-modal" className="max-w-md rounded-3xl text-center py-10">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5"><Loader2 className="w-8 h-8 text-[#D97706] animate-spin" /></div>
        <h3 className="font-head text-2xl font-semibold text-[#041E26]">You&apos;re in the waiting room</h3>
        <p className="text-slate-500 mt-2 px-6">Waiting for <span className="font-semibold text-[#041E26]">{appointment.doctor_name}</span> to admit you.</p>
        <Badge className={`mt-5 ${statusStyle[status] || ""}`}>{statusLabel[status] || status}</Badge>
      </DialogContent>
    </Dialog>
  );
}

export default function PatientPortal() {
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState("all");
  const [providers, setProviders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [booking, setBooking] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [reschedule, setReschedule] = useState(null);
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    const [s, p, a] = await Promise.all([api.get("/sections"), api.get("/providers"), api.get("/appointments")]);
    setSections(s.data.sections); setProviders(p.data.providers); setAppointments(a.data.appointments);
  };
  useEffect(() => { load(); }, []);

  const cancel = async (a) => {
    if (!window.confirm("Cancel this appointment?")) return;
    try { await api.post(`/appointments/${a.id}/cancel`); toast.success("Appointment cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  const filtered = active === "all" ? providers : providers.filter((p) => p.section === active);

  return (
    <div>
      <section className="mb-12">
        <h2 className="font-head text-2xl font-semibold text-[#041E26] mb-5">My appointments</h2>
        {appointments.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No appointments yet. Book one below.</div>
        ) : (
          <div className="space-y-3">
            {appointments.map((a) => (
              <div key={a.id} data-testid={`appt-row-${a.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <img src={a.doctor_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold text-[#041E26]">{a.doctor_name} <span className="text-slate-400 font-normal text-sm">· {a.doctor_specialty}</span></div>
                    <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-2"><CalIcon className="w-3.5 h-3.5" />{fmt(a.slot_time)} · ₹{a.fee}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={statusStyle[a.status] || ""}>{statusLabel[a.status] || a.status}</Badge>
                  {a.status === "pending_payment" && (
                    <Button data-testid={`pay-appt-${a.id}`} onClick={() => setPayFor(a)} className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-semibold gap-2"><CreditCard className="w-4 h-4" /> Pay ₹{a.fee}</Button>
                  )}
                  {["booked", "waiting", "admitted", "in_call"].includes(a.status) && (
                    <>
                      <Button data-testid={`join-appt-${a.id}`} onClick={() => setWaiting(a)} className="rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white font-semibold gap-2"><Video className="w-4 h-4" /> Join</Button>
                      <Button data-testid={`reschedule-appt-${a.id}`} onClick={() => setReschedule(a)} variant="outline" className="rounded-full gap-2"><RefreshCw className="w-4 h-4" /> Reschedule</Button>
                    </>
                  )}
                  {!["completed", "cancelled", "in_call"].includes(a.status) && (
                    <Button data-testid={`cancel-appt-${a.id}`} onClick={() => cancel(a)} variant="outline" className="rounded-full gap-2 text-red-500 border-red-200 hover:bg-red-50"><X className="w-4 h-4" /> Cancel</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <PatientMedicalRecords />

      <section>
        <h2 className="font-head text-2xl font-semibold text-[#041E26] mb-1">Find a provider</h2>
        <p className="text-slate-500 mb-5">Book an appointment by care category.</p>
        <div className="flex flex-wrap gap-2 mb-6">
          <button data-testid="section-all" onClick={() => setActive("all")} className={`px-4 py-2 rounded-full text-sm font-medium ${active === "all" ? "bg-[#041E26] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>All</button>
          {sections.map((s) => (
            <button key={s.id} data-testid={`section-${s.id}`} onClick={() => setActive(s.id)} className={`px-4 py-2 rounded-full text-sm font-medium ${active === s.id ? "bg-[#041E26] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{s.title}</button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No providers in this category yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filtered.map((d, i) => (
              <motion.div key={d.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <img src={d.photo} alt={d.name} className="w-full h-40 object-cover" />
                <div className="p-5">
                  <div className="flex items-center gap-1 text-xs text-green-600 font-semibold mb-1"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</div>
                  <h3 className="font-semibold text-[#041E26]">{d.name}</h3>
                  <p className="text-sm text-[#D97706] font-medium">{d.specialty}</p>
                  <p className="text-xs text-slate-400 mt-1">{d.qualification}</p>
                  {d.provider_type === "therapist" && d.modalities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {d.modalities.slice(0, 3).map((m) => <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{m}</span>)}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-500"><span>{d.experience} yrs</span><span className="font-semibold text-[#041E26]">₹{d.fee}</span></div>
                  {(d.address_city || d.address_line) && (
                    <div data-testid={`provider-address-${d.id}`} className="flex items-start gap-1.5 mt-2 text-[11px] text-slate-500 leading-snug">
                      <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#B45309]" />
                      <span>{[d.address_line, d.address_city, d.address_state, d.address_pincode].filter(Boolean).join(", ")}</span>
                    </div>
                  )}
                  <Button data-testid={`view-provider-${d.id}`} onClick={() => setBooking(d)} className="w-full mt-4 rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white gap-2"><CalIcon className="w-4 h-4" /> Book appointment</Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {booking && <BookingDialog provider={booking} onClose={() => setBooking(null)} onDone={() => { setBooking(null); load(); }} />}
      {payFor && <CompletePaymentDialog appointment={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />}
      {reschedule && <RescheduleDialog appointment={reschedule} onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); load(); }} />}
      {waiting && <WaitingRoom appointment={waiting} onClose={() => { setWaiting(null); load(); }} />}
    </div>
  );
}
